import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Search, Eye, RotateCcw, Save, X, ChevronDown, ChevronUp,
  Sparkles, AlertTriangle, Inbox, Monitor, Tag
} from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../components/common/Toast';
import './EmailTemplates.css';

const CATEGORY_LABELS = {
  pto_lifecycle: 'PTO Request Lifecycle',
  admin_actions: 'Manager & Admin Actions',
  bonus: 'Bonus Awards',
  birthday: 'Birthday',
  year_end: 'PTO Year-End',
  system: 'System',
  holiday: 'Holiday',
  admin_alerts: 'Admin Alerts',
};

const STYLE_CONFIG = {
  standard: { label: 'Standard', icon: Mail, className: 'style-standard' },
  celebratory: { label: 'Celebratory', icon: Sparkles, className: 'style-celebratory' },
  urgent: { label: 'Urgent', icon: AlertTriangle, className: 'style-urgent' },
  digest: { label: 'Digest', icon: Inbox, className: 'style-digest' },
  system: { label: 'System', icon: Monitor, className: 'style-system' },
};

function EmailTemplates() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set(Object.keys(CATEGORY_LABELS)));
  const [statusFilter, setStatusFilter] = useState('');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/email-templates');
      setTemplates(res.templates || []);
    } catch (err) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate({
      ...template,
      subject_template: template.subject_template,
      body_template: template.body_template,
    });
    setPreviewHtml(null);
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editingTemplate.id}`, {
        subject_template: editingTemplate.subject_template,
        body_template: editingTemplate.body_template,
        is_active: editingTemplate.is_active,
      });
      toast.success('Template saved');
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateId) => {
    if (!window.confirm('Reset this template to its default content? Your customizations will be lost.')) return;
    try {
      await api.post(`/admin/email-templates/${templateId}/reset`);
      toast.success('Template reset to defaults');
      if (editingTemplate && editingTemplate.id === templateId) {
        setEditingTemplate(null);
      }
      loadTemplates();
    } catch (err) {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = async (templateId) => {
    try {
      const res = await api.post(`/admin/email-templates/${templateId}/preview`);
      setPreviewHtml(res.preview.fullHtml);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/admin/email-templates/${template.id}`, {
        is_active: !template.is_active,
      });
      toast.success(template.is_active ? 'Template disabled' : 'Template enabled');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  // Group templates by category
  const grouped = {};
  const filteredTemplates = templates.filter(t => {
    // Status filter
    if (statusFilter === 'active' && !t.is_active) return false;
    if (statusFilter === 'disabled' && t.is_active) return false;
    // Search filter
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q)
      || t.notification_type.toLowerCase().includes(q)
      || t.description?.toLowerCase().includes(q);
  });

  for (const t of filteredTemplates) {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  }

  // Sort categories by the order defined in CATEGORY_LABELS
  const categoryOrder = Object.keys(CATEGORY_LABELS);
  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  if (loading) {
    return (
      <div className="admin-email-templates">
        <div className="page-header">
          <Mail size={22} strokeWidth={2} />
          <h1>Email Templates</h1>
        </div>
        <div className="templates-loading">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="admin-email-templates">
      <div className="page-header">
        <Mail size={22} strokeWidth={2} />
        <h1>Email Templates</h1>
      </div>

      <p className="page-description">
        Manage the email templates used for all FPS OS notifications. Edit subject lines and body content, preview with sample data, or reset to defaults.
      </p>

      {/* Summary bar */}
      <div className="templates-summary">
        <span className="templates-summary-stat">
          <span className="status-dot status-dot--active" />
          <strong>{templates.filter(t => t.is_active).length}</strong> active
        </span>
        <span className="templates-summary-stat">
          <span className="status-dot status-dot--disabled" />
          <strong>{templates.filter(t => !t.is_active).length}</strong> disabled
        </span>
        <span className="templates-summary-stat templates-summary-total">
          {templates.length} total
        </span>
      </div>

      {/* Search + Filter */}
      <div className="templates-toolbar">
        <div className="templates-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="templates-filter">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active only</option>
            <option value="disabled">Disabled only</option>
          </select>
        </div>
      </div>

      {/* Template Categories */}
      {sortedCategories.map(category => {
        const isExpanded = expandedCategories.has(category);
        const categoryTemplates = grouped[category];

        return (
          <div key={category} className="template-category">
            <button
              className="template-category-header"
              onClick={() => toggleCategory(category)}
            >
              <span className="template-category-title">
                {CATEGORY_LABELS[category] || category}
                <span className="template-category-count">
                  {categoryTemplates.filter(t => t.is_active).length}/{categoryTemplates.length} active
                </span>
              </span>
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {isExpanded && (
              <div className="template-category-list">
                {categoryTemplates.map(template => {
                  const styleConfig = STYLE_CONFIG[template.template_style] || STYLE_CONFIG.standard;
                  const StyleIcon = styleConfig.icon;

                  return (
                    <div key={template.id} className={`template-card ${!template.is_active ? 'template-card--disabled' : ''}`}>
                      <div className="template-card-header">
                        <label
                          className="template-toggle"
                          title={template.is_active ? 'Active -- click to disable' : 'Disabled -- click to enable'}
                        >
                          <input
                            type="checkbox"
                            checked={template.is_active}
                            onChange={() => handleToggleActive(template)}
                          />
                          <span className="template-toggle-slider" />
                        </label>
                        <div className="template-card-info">
                          <div className="template-card-name">
                            {template.name}
                            <span className={`template-status-label ${template.is_active ? 'active' : 'disabled'}`}>
                              {template.is_active ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                          <div className="template-card-type">{template.notification_type}</div>
                        </div>
                        <div className="template-card-badges">
                          <span className={`template-style-badge ${styleConfig.className}`}>
                            <StyleIcon size={12} />
                            {styleConfig.label}
                          </span>
                        </div>
                      </div>

                      {template.description && (
                        <div className="template-card-desc">{template.description}</div>
                      )}

                      <div className="template-card-subject">
                        <strong>Subject:</strong> {template.subject_template}
                      </div>

                      {template.merge_tags && template.merge_tags.length > 0 && (
                        <div className="template-card-tags">
                          <Tag size={12} />
                          {template.merge_tags.map(tag => (
                            <code key={tag} className="merge-tag">{`{{${tag}}}`}</code>
                          ))}
                        </div>
                      )}

                      <div className="template-card-actions">
                        <button className="btn-sm btn-primary" onClick={() => handleEdit(template)}>
                          Edit
                        </button>
                        <button className="btn-sm btn-outline" onClick={() => handlePreview(template.id)}>
                          <Eye size={14} />
                          Preview
                        </button>
                        <button className="btn-sm btn-outline" onClick={() => handleReset(template.id)}>
                          <RotateCcw size={14} />
                          Reset
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {filteredTemplates.length === 0 && !loading && (
        <div className="templates-empty">No templates match your search.</div>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="template-modal-overlay" onClick={() => setEditingTemplate(null)}>
          <div className="template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h2>Edit Template: {editingTemplate.name}</h2>
              <button className="template-modal-close" onClick={() => setEditingTemplate(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="template-modal-body">
              <div className="template-field">
                <label>Subject Line</label>
                <input
                  type="text"
                  value={editingTemplate.subject_template}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject_template: e.target.value }))}
                />
              </div>

              <div className="template-field">
                <label>
                  Body HTML
                  <span className="template-field-hint">Use {"{{variable}}"} merge tags for dynamic content</span>
                </label>
                <textarea
                  value={editingTemplate.body_template}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, body_template: e.target.value }))}
                  rows={16}
                />
              </div>

              {editingTemplate.merge_tags && editingTemplate.merge_tags.length > 0 && (
                <div className="template-available-tags">
                  <strong>Available merge tags:</strong>
                  <div className="template-tags-list">
                    {editingTemplate.merge_tags.map(tag => (
                      <code key={tag} className="merge-tag">{`{{${tag}}}`}</code>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="template-modal-footer">
              <button className="btn-sm btn-outline" onClick={() => setEditingTemplate(null)}>
                Cancel
              </button>
              <button className="btn-sm btn-outline" onClick={() => handlePreview(editingTemplate.id)}>
                <Eye size={14} />
                Preview
              </button>
              <button className="btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={14} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div className="template-modal-overlay" onClick={() => setPreviewHtml(null)}>
          <div className="template-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h2>Email Preview</h2>
              <button className="template-modal-close" onClick={() => setPreviewHtml(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="template-preview-frame">
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                style={{ width: '100%', height: '600px', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplates;
